import React, { useState, useRef, useEffect } from 'react';
import { Smile } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const EMOJI_CATEGORY_KEYS = ['frequentlyUsed', 'smileysAndPeople', 'gestures', 'hearts', 'objects', 'symbols'];

const EMOJI_CATEGORIES = {
    'frequentlyUsed': ['😀', '😂', '😍', '🥰', '😊', '😎', '🤔', '👍', '❤️', '🔥'],
    'smileysAndPeople': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔'],
    'gestures': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝'],
    'hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
    'objects': ['⌚', '📱', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️'],
    'symbols': ['✅', '❌', '⭐', '🌟', '💫', '✨', '💥', '💢', '💤', '💨', '🔥', '💧', '🌊', '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈']
};

const EmojiPicker = ({ onEmojiSelect, position = 'top' }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState('frequentlyUsed');
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleEmojiClick = (emoji, e) => {
        e.preventDefault();
        e.stopPropagation();
        onEmojiSelect(emoji);
        // Keep picker open so user can select multiple emojis
    };

    return (
        <div className="relative" ref={pickerRef} data-testid="emoji-picker-container">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                aria-label={t('emojiPicker.addEmoji')}
                data-testid="emoji-picker-toggle-button"
            >
                <Smile size={18} className="text-gray-400 hover:text-[#4ABDBD] transition-colors" data-testid="emoji-picker-smile-icon" />
            </button>

            {isOpen && (
                <div
                    className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} right-0 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 w-80 sm:w-96 max-w-[calc(100vw-2rem)] max-h-96 flex flex-col overflow-hidden`}
                    data-testid="emoji-picker-dropdown"
                >
                    {/* Categories */}
                    <div className="flex flex-nowrap border-b border-gray-200 bg-gray-50 overflow-x-auto custom-scrollbargray [&::-webkit-scrollbar-button]:hidden" data-testid="emoji-picker-categories">
                            {EMOJI_CATEGORY_KEYS.map((categoryKey) => (
                                <button
                                    key={categoryKey}
                                    onClick={() => setActiveCategory(categoryKey)}
                                    className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                                        activeCategory === categoryKey
                                            ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD] bg-white'
                                            : 'text-gray-600 hover:text-[#4ABDBD]'
                                    }`}
                                    data-testid={`emoji-picker-tab-${categoryKey}`}
                                >
                                    {t(`emojiPicker.${categoryKey}`)}
                                </button>
                            ))}
                        </div>

                        {/* Emojis Grid */}
                        <div className="flex-1 overflow-y-auto p-3 custom-scrollbar" data-testid="emoji-picker-grid-container">
                            <div className="grid grid-cols-8 gap-2" data-testid="emoji-picker-grid">
                                {EMOJI_CATEGORIES[activeCategory].map((emoji, index) => (
                                    <button
                                        key={`${emoji}-${index}`}
                                        type="button"
                                        onClick={(e) => handleEmojiClick(emoji, e)}
                                        className="w-8 h-8 flex items-center justify-center text-xl hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                        aria-label={t('emojiPicker.selectEmoji', { emoji })}
                                        data-testid={`emoji-picker-option-${index}`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default EmojiPicker;

